<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ExpertProfileStatus } from '@shopexperts/legacy-api';

export interface ExpertStatsSegment {
  key: ExpertProfileStatus;
  label: string;
  count: number;
  pct: number;
  colorClass: string;
  textClass: string;
}

const props = withDefaults(defineProps<{
  segments: ExpertStatsSegment[];
  activeKey?: string;
  ariaLabelPrefix?: string;
  itemVariant?: 'legend' | 'chip';
  minSegmentPct?: number;
}>(), {
  itemVariant: 'legend',
  minSegmentPct: 1.5,
});

const emit = defineEmits<{
  select: [status: ExpertProfileStatus];
  hoverChange: [status: ExpertProfileStatus | null];
}>();

const hoveredKey = ref<ExpertProfileStatus | null>(null);

const itemClassName = computed(() => (
  props.itemVariant === 'chip'
    ? 'inline-flex items-center gap-1.5 rounded-full bg-transparent px-2 py-1 text-[11px] ring-0 transition-[background-color,color,opacity] duration-200'
    : 'inline-flex items-center gap-1.5 rounded-full bg-transparent px-2 py-1 text-xs ring-0 transition-[background-color,color,opacity] duration-200'
));

function isEmphasized(status: ExpertProfileStatus) {
  return hoveredKey.value === status || props.activeKey === status;
}

function isDimmed(status: ExpertProfileStatus) {
  return hoveredKey.value !== null && hoveredKey.value !== status;
}

function setHoveredKey(status: ExpertProfileStatus | null) {
  hoveredKey.value = status;
  emit('hoverChange', status);
}

function segmentClasses(status: ExpertProfileStatus) {
  return [
    'relative h-full rounded-full border-0 p-0 transform-gpu cursor-pointer origin-center transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300',
    isEmphasized(status) ? 'z-10 -translate-y-px scale-y-[1.8] saturate-125 brightness-105' : '',
    isDimmed(status) ? 'opacity-25 grayscale saturate-50 brightness-90' : '',
  ];
}

function itemClasses(status: ExpertProfileStatus) {
  return [
    itemClassName.value,
    isEmphasized(status) ? 'bg-slate-50 ring-1 ring-slate-200 shadow-sm' : '',
    isDimmed(status) ? 'opacity-40' : 'hover:bg-slate-50 hover:ring-1 hover:ring-slate-200 hover:shadow-sm',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300',
  ];
}
</script>

<template>
  <div class="flex min-w-0 flex-col gap-2.5">
    <div class="flex h-2 isolate gap-[1.5px] overflow-visible rounded-full bg-gray-100">
      <button
        v-for="segment in segments"
        :key="segment.key"
        type="button"
        :class="[segment.colorClass, ...segmentClasses(segment.key)]"
        :style="{ width: `${Math.max(segment.pct, minSegmentPct)}%` }"
        :aria-label="`${ariaLabelPrefix} ${segment.label}`"
        @mouseenter="setHoveredKey(segment.key)"
        @mouseleave="setHoveredKey(null)"
        @focus="setHoveredKey(segment.key)"
        @blur="setHoveredKey(null)"
        @click="emit('select', segment.key)"
      />
    </div>

    <div class="flex flex-wrap gap-x-4 gap-y-3">
      <button
        v-for="segment in segments"
        :key="segment.key"
        type="button"
        :class="itemClasses(segment.key)"
        :aria-label="`${ariaLabelPrefix} ${segment.label}`"
        @mouseenter="setHoveredKey(segment.key)"
        @mouseleave="setHoveredKey(null)"
        @focus="setHoveredKey(segment.key)"
        @blur="setHoveredKey(null)"
        @click="emit('select', segment.key)"
      >
        <span class="h-1.5 w-1.5 shrink-0 rounded-full" :class="segment.colorClass" />
        <span class="text-gray-500 transition-colors duration-200" :class="{ 'text-gray-900': isEmphasized(segment.key) }">
          {{ segment.label }}
        </span>
        <span class="tabular-nums font-bold" :class="segment.textClass">{{ segment.count }}</span>
      </button>
    </div>
  </div>
</template>
